import { ShiftRosterService, isAdministrativeRole } from './shiftRoster';
import { LeaveService } from './leave';
import { OvertimeService } from './overtimeService';
import { loadAllUsers, accountIdsFor, SimpleUser } from './userLoader';
import { sameIdentity } from './identity';
import { getHolidayForDate } from '../data/holidays';
import { resolveLeaveCode } from '../components/shift-roster/shiftRosterDisplay';
import { LeaveRequest, LeaveStatus } from '../types/leave';
import { ShiftAssignment, ShiftMaster } from '../types/shiftRoster';
import {
  ShiftBand,
  ShiftReportFilters,
  ShiftReportRecord,
  ShiftReportSummary,
  ShiftReportType,
  ShiftSource,
  reportMeta,
} from '../types/shiftReport';

/**
 * Shift Roster → Reports: data layer.
 *
 * Three stages, deliberately separate so each is testable without a network: `loadRecords` builds
 * the employee-day fact table for a date range, `applyFilters` narrows it, and the `buildRows`
 * family projects it into whichever of the fifteen reports was asked for. Filtering never touches
 * the network, so changing a filter re-renders from data already in hand instead of re-fetching.
 */

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKLY_OFF_SHIFT_CODE = 'OFF';

/** Local-time ISO date. `toISOString()` would shift the day for anyone east or west of UTC. */
const isoDate = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Parsed as local midnight — `new Date('2026-08-01')` is UTC midnight, a day earlier in IST. */
const parseIso = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const eachDate = (fromIso: string, toIso: string): string[] => {
  const out: string[] = [];
  if (!fromIso || !toIso || fromIso > toIso) return out;
  const end = parseIso(toIso);
  for (const cur = parseIso(fromIso); cur <= end; cur.setDate(cur.getDate() + 1)) {
    out.push(isoDate(cur));
  }
  return out;
};

/**
 * ISO-8601 week label, e.g. `2026-W34`.
 *
 * Used only to group the Weekly report, but computed properly rather than as
 * `floor(dayOfMonth / 7)`: the cheap version splits the same Monday-to-Sunday week across two
 * groups whenever a month boundary falls mid-week, which is most months.
 */
export const isoWeekLabel = (iso: string): string => {
  const d = parseIso(iso);
  // Thursday of the current ISO week determines the year the week belongs to.
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayNum = (target.getDay() + 6) % 7; // Monday = 0
  target.setDate(target.getDate() - dayNum + 3);
  const isoYear = target.getFullYear();
  const firstThursday = new Date(isoYear, 0, 4);
  const firstDayNum = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNum + 3);
  const week = 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
};

/** Weekly-off days for a shift. `[0, 6]` — Sun + Sat — when nothing is configured. */
const weekendDaysFor = (shift?: ShiftMaster): number[] =>
  shift?.weekendDays && shift.weekendDays.length > 0 ? shift.weekendDays : [0, 6];

/** Start hour from an `HH:MM` string; -1 when unparseable, so callers can tell "unknown". */
const startHour = (time: string): number => {
  const m = /^(\d{1,2}):(\d{2})/.exec(time || '');
  return m ? Number(m[1]) : -1;
};

const derivesNightShift = (start: string, end: string): boolean => {
  const s = startHour(start);
  const e = startHour(end);
  if (s < 0 || e < 0) return false;
  return e <= s; // crosses midnight
};

/**
 * Which band a worked shift falls in.
 *
 * The order is the definition, and each step is there for a reason:
 *  - An explicitly configured night shift is Night whatever the clock says, because `isNightShift`
 *    is an override HR can set and second-guessing it from the times would ignore them.
 *  - General is matched by identity (code or name), not by hour, because the General shift starts
 *    at 09:00 in this organisation and so does a Morning shift — the hour cannot separate them.
 *  - Only then does the start hour decide, and a shift crossing midnight counts as Night.
 */
/**
 * The bands that mean "this day was worked". The rest — Weekly Off, Holiday, Leave, Unassigned —
 * are the reasons a day was not.
 *
 * Exported because the preview's tiles assert the split outright ("N employee-days = W worked +
 * X not worked"), and that claim is only true if one definition decides both halves.
 */
export const WORKED_BANDS: ShiftBand[] = ['Morning', 'General', 'Evening', 'Night'];

export const isWorkedBand = (band: ShiftBand): boolean => WORKED_BANDS.includes(band);

/**
 * The bands that mean the employee was off — nobody was expected at work.
 *
 * Not simply `!isWorkedBand`: an Unassigned day is a working day that nobody rostered, which is a
 * roster gap rather than time off. It stays in a "working days only" selection precisely so the gap
 * is visible; filtering it out would hide the problem the Unassigned report exists to find.
 */
const DAY_OFF_BANDS: ShiftBand[] = ['Weekly Off', 'Holiday', 'Leave'];

export const isDayOffBand = (band: ShiftBand): boolean => DAY_OFF_BANDS.includes(band);

export const bandForShift = (shift: ShiftMaster | undefined, code: string): ShiftBand => {
  if (shift) {
    if (shift.isNightShift === true) return 'Night';
    const name = `${shift.shiftCode} ${shift.shiftName}`.toLowerCase();
    if (/\bgen\b|general/.test(name)) return 'General';
    if (shift.isNightShift !== false && derivesNightShift(shift.startTime, shift.endTime)) return 'Night';
    const h = startHour(shift.startTime);
    if (h < 0) return 'General';
    if (h < 12) return 'Morning';
    if (h < 17) return 'Evening';
    return 'Night';
  }
  // A code with no matching shift master — a stale override, or a definition since deleted.
  // Reported as its own band rather than guessed at.
  return /\bgen\b/i.test(code) ? 'General' : 'Unassigned';
};

/**
 * Whether the employee was on the roster as at `asOfIso`.
 *
 * A disabled account is never active, whatever the date. An exit date on or before `asOfIso` means
 * they had already left by then; one after it had not happened yet. Callers pass the end of the
 * period being reported on, not today — see the call site in loadRecords.
 */
const isActiveEmployee = (user: SimpleUser, asOfIso: string): boolean => {
  if (user.enabled === false) return false;
  const exit = (user.dateOfExit || '').trim();
  return !(exit && exit <= asOfIso);
};

/**
 * Run `fn` over `items` at most `limit` at a time.
 *
 * The roster grid fires one request per employee with a bare `Promise.all`, which is tolerable for
 * one month. A report over a year does the same work per employee but paginated, and letting two
 * hundred of those start at once gets the browser's connection pool thrashing and AppSync
 * throttling — the report then fails in a way that looks like a permissions problem.
 */
const mapWithLimit = async <I, O>(
  items: I[],
  limit: number,
  fn: (item: I, index: number) => Promise<O>,
  onProgress?: (done: number, total: number) => void
): Promise<O[]> => {
  const out: O[] = new Array(items.length);
  let cursor = 0;
  let done = 0;
  const worker = async (): Promise<void> => {
    while (cursor < items.length) {
      const i = cursor++;
      out[i] = await fn(items[i], i);
      done++;
      onProgress?.(done, items.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
};

// ─── Range resolution ─────────────────────────────────────────────────────────

export interface ResolvedRange {
  from: string;
  to: string;
  label: string;
}

/**
 * The date window to fetch, from whichever of the three pickers the Admin used.
 *
 * Explicit From/To wins over the Month/Year shortcut, because someone who typed dates meant them.
 * A half-filled From/To (only one end) is completed from the other end rather than ignored, which
 * is what "from 1 August" plainly means.
 */
export const resolveRange = (filters: ShiftReportFilters): ResolvedRange => {
  const { dateFrom, dateTo, month, year } = filters;

  if (dateFrom && dateTo) {
    const [from, to] = dateFrom <= dateTo ? [dateFrom, dateTo] : [dateTo, dateFrom];
    return { from, to, label: `${from} to ${to}` };
  }
  if (dateFrom && !dateTo) {
    const to = isoDate(new Date(parseIso(dateFrom).getFullYear(), parseIso(dateFrom).getMonth() + 1, 0));
    return { from: dateFrom, to: to < dateFrom ? dateFrom : to, label: `${dateFrom} to ${to}` };
  }
  if (!dateFrom && dateTo) {
    const from = `${dateTo.slice(0, 7)}-01`;
    return { from, to: dateTo, label: `${from} to ${dateTo}` };
  }

  const y = year || new Date().getFullYear();
  if (month >= 0 && month <= 11) {
    const last = new Date(y, month + 1, 0).getDate();
    const p = (n: number) => String(n).padStart(2, '0');
    return {
      from: `${y}-${p(month + 1)}-01`,
      to: `${y}-${p(month + 1)}-${p(last)}`,
      label: `${MONTH_NAMES[month]} ${y}`,
    };
  }
  return { from: `${y}-01-01`, to: `${y}-12-31`, label: `Year ${y}` };
};

// ─── Stage 1: the fact table ──────────────────────────────────────────────────

export interface LoadRecordsResult {
  records: ShiftReportRecord[];
  shifts: ShiftMaster[];
  employees: SimpleUser[];
  range: ResolvedRange;
  /** Set when the detailed assignment fields were unavailable, for the UI to surface. */
  degraded: boolean;
  /**
   * Employees whose assignments could not be read at all, by name.
   *
   * Their rows are still built — from the primary shift, the weekly off and the holiday
   * calendar — because omitting them would understate every headcount on the page. But those
   * rows cannot show an override that exists, so an employee who worked a Saturday reads as
   * having taken it off, and every figure derived from them is wrong in the same direction.
   *
   * Reported rather than logged: a report that quietly substitutes "no shifts" for "the query
   * failed" is indistinguishable from one that is simply wrong, and nobody can tell by looking.
   */
  unreadable: string[];
}

export interface LoadRecordsOptions {
  onProgress?: (done: number, total: number) => void;
}

/**
 * Build the employee-day table for a range.
 *
 * Every employee is included and every date in the range is emitted for each of them, whether or
 * not an assignment exists — a day with no shift is exactly what the Unassigned report is for, so
 * dropping empty days here would make that report permanently empty.
 *
 * Reads org-wide. That matches the page's access rule rather than widening it: Reports is
 * HR_Admin / Super_Admin only, and both already see every employee in Shift Details and through
 * loadAllUsers. No role reaches this code that could not already read the same rows.
 */
export const loadRecords = async (
  filters: ShiftReportFilters,
  options: LoadRecordsOptions = {}
): Promise<LoadRecordsResult> => {
  const range = resolveRange(filters);
  const dates = eachDate(range.from, range.to);
  const todayIso = isoDate(new Date());

  const [directory, shifts] = await Promise.all([
    loadAllUsers(),
    ShiftRosterService.listShiftMasters(),
  ]);

  /*
   * The same population Shift Details shows.
   *
   * Administrative logins — Super_Admin, HR_Admin, Finance_Admin — are operator accounts, not
   * people who work a shift. Leaving them in made every headcount on this page disagree with the
   * grid: "Total Employees" counted them, the Employee-wise report gave them a row of weekly offs,
   * and a Department-wise total was inflated by accounts belonging to no department. Two screens
   * reading the same table have to agree on who is in it.
   */
  const employees = directory.filter(e => !isAdministrativeRole(e.role));

  // Leave and overtime are whole-directory reads with no date-range parameter, so they are
  // fetched once and indexed, not queried per employee.
  const [leaves] = await Promise.all([
    LeaveService.listLeaves().catch(() => [] as LeaveRequest[]),
    OvertimeService.refresh().catch(() => false),
  ]);
  const approvedLeaves = (leaves || []).filter(
    (l: any) => String(l.status || '').toUpperCase() === 'APPROVED' || l.status === LeaveStatus.APPROVED
  );
  const overtimeRecords = OvertimeService.getRecords();

  const shiftByCode = new Map(shifts.map(s => [s.shiftCode, s]));

  /*
   * A per-employee failure is recorded, not swallowed.
   *
   * getAssignmentsDetailedForAccounts throws when it could not read an employee at all. Letting
   * that reject would lose the whole report for one bad employee; catching it silently — which is
   * what happened before it threw — turns that employee's worked days into weekly offs. So the
   * failure is caught here, the employee still gets rows built from their primary shift, and the
   * name goes into `unreadable` for the page to declare.
   */
  const unreadable: string[] = [];
  const assignmentPages = await mapWithLimit(
    employees,
    6,
    async emp => {
      try {
        return await ShiftRosterService.getAssignmentsDetailedForAccounts(
          accountIdsFor(emp), range.from, range.to
        );
      } catch (error) {
        console.error(
          `[ShiftReports] Could not read assignments for ${emp.email || emp.id}:`, error
        );
        unreadable.push(emp.fullName || emp.name || emp.email || emp.id);
        return [] as ShiftAssignment[];
      }
    },
    options.onProgress
  );

  const records: ShiftReportRecord[] = [];

  employees.forEach((emp, idx) => {
    const byDate = new Map<string, ShiftAssignment>();
    for (const a of assignmentPages[idx] || []) byDate.set(a.date, a);

    const primaryCode = (emp.primaryShift || '').trim();
    const primaryShift = shiftByCode.get(primaryCode);
    const weekendDays = weekendDaysFor(primaryShift);

    // Both are per-employee, so the whole-directory lists are narrowed once here rather than
    // being re-scanned for every one of up to 366 dates.
    const empLeaves = approvedLeaves.filter((l: LeaveRequest) => sameIdentity(l.employeeEmail, emp.email));
    const empOvertime = overtimeRecords.filter(o => sameIdentity(o.employeeEmail, emp.email));

    /*
     * Active *as at the end of the period being reported on*, not as at today.
     *
     * Keyed on today, a report for August run in December marked everyone who left in between as
     * Inactive for their August rows — so "Active only" silently dropped people who were on the
     * roster for every day the report covers. The exit date is the fact; which date it is compared
     * against is what makes the answer about the report's period rather than about the moment
     * someone happened to open the page.
     */
    const active = isActiveEmployee(emp, range.to || todayIso);

    for (const date of dates) {
      const d = parseIso(date);
      const weekday = d.getDay();
      const holiday = getHolidayForDate(date);
      const assignment = byDate.get(date);
      const overrideCode = (assignment?.shiftCode || '').trim();
      const leave = empLeaves.find(l => date >= l.startDate && date <= l.endDate);
      const ot = empOvertime.find(o => o.date === date);
      const isWeekend = weekendDays.includes(weekday);

      /*
       * Precedence, matching what Shift Details paints on the grid so the two screens cannot
       * disagree about what a day was: approved leave, then a manual override (which deliberately
       * outranks a holiday and a weekly off — an override on a holiday is someone rostered to
       * work it), then the holiday, then the weekly off, then the employee's primary shift.
       */
      let code = '';
      let source: ShiftSource;
      let band: ShiftBand;

      if (leave) {
        code = resolveLeaveCode(leave.type);
        source = 'Leave';
        band = 'Leave';
      } else if (overrideCode && overrideCode !== WEEKLY_OFF_SHIFT_CODE) {
        code = overrideCode;
        source = 'Override';
        band = bandForShift(shiftByCode.get(overrideCode), overrideCode);
      } else if (overrideCode === WEEKLY_OFF_SHIFT_CODE) {
        code = WEEKLY_OFF_SHIFT_CODE;
        source = 'Weekly Off';
        band = 'Weekly Off';
      } else if (holiday) {
        code = WEEKLY_OFF_SHIFT_CODE;
        source = 'Holiday';
        band = 'Holiday';
      } else if (isWeekend) {
        code = WEEKLY_OFF_SHIFT_CODE;
        source = 'Weekly Off';
        band = 'Weekly Off';
      } else if (primaryCode) {
        code = primaryCode;
        source = 'Primary';
        band = bandForShift(primaryShift, primaryCode);
      } else {
        code = '';
        source = 'Unassigned';
        band = 'Unassigned';
      }

      const shift = code ? shiftByCode.get(code) : undefined;
      /*
       * Keyed on the band, not on the source.
       *
       * `source === 'Override' || 'Primary'` disagreed with the band for one real case: an
       * assignment naming a shift code whose master has since been deleted or renamed. bandForShift
       * reports that as Unassigned — correctly, it cannot invent a band — but the day still had a
       * source of Override, so it counted as worked *and* as "no shift set" at the same time. That
       * makes the identity the preview prints under its tiles ("employee-days = worked + not
       * worked") arithmetically false, and it put the day in the Total Shifts count while the
       * Unassigned report, which selected on source, left it out.
       */
      const isWorkingDay = isWorkedBand(band);

      records.push({
        employeeId: emp.id,
        employeeCode: emp.employeeId || '',
        employeeName: emp.fullName || emp.name || '',
        employeeEmail: emp.email || '',
        designation: emp.designation || '',
        jobTitle: emp.department || '',
        team: emp.team || '',
        location: emp.location || '',
        managerEmail: emp.managerEmail || '',
        employmentType: emp.employmentType || '',
        employeeActive: active,
        dateOfJoining: emp.dateOfJoining || '',
        dateOfExit: emp.dateOfExit || '',

        date,
        weekday,
        weekdayName: WEEKDAY_NAMES[weekday],
        month: d.getMonth(),
        year: d.getFullYear(),
        isoWeek: isoWeekLabel(date),
        isWeekend,
        holidayName: holiday?.name || '',
        isHoliday: !!holiday,

        shiftCode: code,
        shiftName: shift?.shiftName || (source === 'Leave' ? `${code} (Leave)` : ''),
        startTime: isWorkingDay ? shift?.startTime || '' : '',
        endTime: isWorkingDay ? shift?.endTime || '' : '',
        band,
        source,
        workingHours: isWorkingDay && shift?.workingHours != null ? shift.workingHours : null,
        breakDuration: isWorkingDay && shift?.breakDuration != null ? shift.breakDuration : null,
        overtimeEligible: !!shift?.overtimeEligible,
        isWorkingDay,

        status: assignment?.status || '',
        rosterStatus: assignment?.rosterStatus || '',
        previousShiftCode: assignment?.previousShiftCode || '',
        changeReason: assignment?.changeReason || '',
        changed: !!assignment?.previousShiftCode &&
                 assignment.previousShiftCode !== assignment.shiftCode,
        createdBy: assignment?.createdBy || '',
        createdAt: assignment?.createdAt || '',
        updatedBy: assignment?.updatedBy || '',
        updatedAt: assignment?.updatedAt || '',

        leaveType: leave ? leave.type : '',
        onLeave: !!leave,

        overtimeHours: ot?.hoursWorked || 0,
        overtimeStatus: ot?.status || '',
        overtimeType: ot?.otType || '',
      });
    }
  });

  return {
    records,
    shifts,
    employees,
    range,
    degraded: !ShiftRosterService.detailedAssignmentFieldsAvailable(),
    unreadable,
  };
};

// ─── Stage 2: filtering ───────────────────────────────────────────────────────

const has = (haystack: string, needle: string): boolean =>
  haystack.toLowerCase().includes(needle.trim().toLowerCase());

/**
 * Narrow the fact table. Every filter is AND-ed, which is the requirement — "Department =
 * Engineering + Shift = Night + Date = August 1–31" has to mean the intersection.
 *
 * The date range is not re-applied here: `loadRecords` only ever built dates inside it.
 */
/**
 * The Department value meaning "employees with no department set".
 *
 * A real filter option rather than a gap: `team` is optional (it lives in UserProfiles, and HR
 * fills it in per employee), so on any real directory a good number of people have none. Without
 * this the only way to see them was to clear the filter, which also brought back everybody else —
 * and the population that most needs finding, since a blank Department is usually an oversight.
 *
 * The same sentinel Shift Details uses for its own department filter, so the two tabs behave
 * identically.
 */
export const NO_DEPARTMENT = '__UNASSIGNED__';

export const applyFilters = (
  records: ShiftReportRecord[],
  filters: ShiftReportFilters
): ShiftReportRecord[] => {
  /*
   * The range is re-applied here, even though loadRecords only ever built dates inside it.
   *
   * The two can disagree for as long as a fetch is in flight: `applied` updates the moment Apply
   * Filters is pressed, while `records` still holds the previous range until the request lands. A
   * preview of August rows under a September heading is a report that cannot be trusted, and the
   * check costs one string comparison per row.
   */
  const range = resolveRange(filters);

  return records.filter(r => {
    if (r.date < range.from || r.date > range.to) return false;

    if (filters.employeeName && !has(`${r.employeeName} ${r.employeeEmail}`, filters.employeeName)) return false;
    if (filters.employeeCode && !has(r.employeeCode, filters.employeeCode)) return false;
    if (filters.team === NO_DEPARTMENT) {
      if (r.team) return false;
    } else if (filters.team && r.team !== filters.team) {
      return false;
    }
    if (filters.employeeStatus === 'ACTIVE' && !r.employeeActive) return false;
    if (filters.employeeStatus === 'INACTIVE' && r.employeeActive) return false;

    if (filters.shiftCode && r.shiftCode !== filters.shiftCode) return false;

    // '0' is Sunday and must count as a constraint — a truthiness check here would ignore it.
    if (filters.specificDay !== '' && String(r.weekday) !== filters.specificDay) return false;
    if (filters.holidayKind === 'HOLIDAY' && !r.isHoliday) return false;
    /*
     * "Working days only" means days the employee was expected at work.
     *
     * It used to exclude public holidays and nothing else, so the selection still carried every
     * Saturday, every weekly off and every day of approved leave — on a normal month it removed
     * one or two rows out of thirty and read as a filter that did nothing at all.
     *
     * A holiday somebody was rostered to work stays in, because they worked it: an override
     * outranks the holiday, so the day's band is a clock band and only `isHoliday` still marks
     * what the date was. That is what the separate "Public holidays only" option is for.
     */
    if (filters.holidayKind === 'WORKING' && isDayOffBand(r.band)) return false;

    return true;
  });
};

/** Counted from the filtered records — never from a page slice, or the tiles would understate. */
export const summarise = (records: ShiftReportRecord[]): ShiftReportSummary => {
  const employees = new Set<string>();
  const summary: ShiftReportSummary = {
    totalEmployees: 0, totalShifts: 0, morning: 0, general: 0, evening: 0, night: 0,
    unassigned: 0, changed: 0, weeklyOff: 0, holiday: 0, onLeave: 0, overtimeHours: 0,
  };

  for (const r of records) {
    employees.add(r.employeeId);
    if (r.isWorkingDay) summary.totalShifts++;
    switch (r.band) {
      case 'Morning': summary.morning++; break;
      case 'General': summary.general++; break;
      case 'Evening': summary.evening++; break;
      case 'Night': summary.night++; break;
      case 'Weekly Off': summary.weeklyOff++; break;
      case 'Holiday': summary.holiday++; break;
      case 'Leave': summary.onLeave++; break;
      case 'Unassigned': summary.unassigned++; break;
    }
    if (r.changed) summary.changed++;
    summary.overtimeHours += r.overtimeHours;
  }

  summary.totalEmployees = employees.size;
  summary.overtimeHours = Math.round(summary.overtimeHours * 100) / 100;
  return summary;
};

// ─── Stage 3: report projections ──────────────────────────────────────────────

/**
 * A row of whichever report is selected.
 *
 * Detail reports put a `ShiftReportRecord` in `record`; summary reports fill `group` and the
 * counts. One row type rather than two keeps the table, the column registry and all three
 * exporters from each needing to branch on report shape.
 */
export interface ShiftReportRow {
  key: string;
  record?: ShiftReportRecord;
  group?: string;
  groupLabel?: string;
  employees?: number;
  days?: number;
  shifts?: number;
  morning?: number;
  general?: number;
  evening?: number;
  night?: number;
  weeklyOff?: number;
  holiday?: number;
  leave?: number;
  unassigned?: number;
  changed?: number;
  overtimeHours?: number;
}

const summariseGroup = (key: string, label: string, rows: ShiftReportRecord[]): ShiftReportRow => {
  const employees = new Set<string>();
  const row: ShiftReportRow = {
    key, group: key, groupLabel: label,
    days: rows.length, shifts: 0, morning: 0, general: 0, evening: 0, night: 0,
    weeklyOff: 0, holiday: 0, leave: 0, unassigned: 0, changed: 0, overtimeHours: 0,
  };
  for (const r of rows) {
    employees.add(r.employeeId);
    if (r.isWorkingDay) row.shifts!++;
    switch (r.band) {
      case 'Morning': row.morning!++; break;
      case 'General': row.general!++; break;
      case 'Evening': row.evening!++; break;
      case 'Night': row.night!++; break;
      case 'Weekly Off': row.weeklyOff!++; break;
      case 'Holiday': row.holiday!++; break;
      case 'Leave': row.leave!++; break;
      case 'Unassigned': row.unassigned!++; break;
    }
    if (r.changed) row.changed!++;
    row.overtimeHours! += r.overtimeHours;
  }
  row.employees = employees.size;
  row.overtimeHours = Math.round(row.overtimeHours! * 100) / 100;
  return row;
};

const groupBy = (
  records: ShiftReportRecord[],
  keyOf: (r: ShiftReportRecord) => string,
  labelOf: (r: ShiftReportRecord) => string
): ShiftReportRow[] => {
  const buckets = new Map<string, { label: string; rows: ShiftReportRecord[] }>();
  for (const r of records) {
    const key = keyOf(r);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { label: labelOf(r), rows: [] };
      buckets.set(key, bucket);
    }
    bucket.rows.push(r);
  }
  return Array.from(buckets.entries())
    .map(([key, b]) => summariseGroup(key, b.label, b.rows))
    .sort((a, b) => a.groupLabel!.localeCompare(b.groupLabel!));
};

const detailRows = (records: ShiftReportRecord[]): ShiftReportRow[] =>
  records
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date) || a.employeeName.localeCompare(b.employeeName))
    .map(r => ({ key: `${r.employeeId}|${r.date}`, record: r }));

/**
 * Project the filtered records into the selected report.
 *
 * The detail reports that narrow further — Unassigned, Holiday/Weekend —
 * do so here rather than in `applyFilters`, because that narrowing is what the report *is*, not
 * something the Admin chose. Keeping it out of the filter stage means the summary tiles above the
 * preview still describe the filter selection, and switching report type does not silently
 * rewrite the filters.
 */
export const buildRows = (
  records: ShiftReportRecord[],
  type: ShiftReportType
): ShiftReportRow[] => {
  switch (type) {
    case 'DAILY':
      return detailRows(records);

    case 'WEEKLY':
      return groupBy(records, r => r.isoWeek, r => r.isoWeek).sort((a, b) => a.group!.localeCompare(b.group!));

    case 'MONTHLY':
      return groupBy(
        records,
        r => `${r.year}-${String(r.month + 1).padStart(2, '0')}`,
        r => `${MONTH_NAMES[r.month]} ${r.year}`
      ).sort((a, b) => a.group!.localeCompare(b.group!));

    case 'EMPLOYEE_WISE':
      return groupBy(
        records,
        r => r.employeeId,
        r => r.employeeName || r.employeeEmail || r.employeeId
      );

    case 'DEPARTMENT_WISE':
      return groupBy(records, r => r.team || '—', r => r.team || 'No department set');

    case 'MANAGER_WISE':
      return groupBy(records, r => r.managerEmail || '—', r => r.managerEmail || 'No manager set');

    case 'SHIFT_WISE':
      // Keyed on the band as well as the code so a stale code and a live shift that happen to
      // share a name do not merge into one row.
      return groupBy(
        records,
        r => `${r.shiftCode || '—'}|${r.band}`,
        r => (r.shiftCode ? `${r.shiftCode}${r.shiftName ? ` — ${r.shiftName}` : ''}` : 'Unassigned')
      );

    case 'HOLIDAY_WEEKEND':
      // Days that are a holiday or a weekly off *and* were actually worked, plus the offs
      // themselves — the question this report answers is "who was on the roster when the office
      // was shut", so a worked holiday is the signal and a plain day off is the context.
      return detailRows(records.filter(r => r.isHoliday || r.isWeekend));

    case 'UNASSIGNED':
      /*
       * The band, not the source. A day whose assignment names a shift code with no surviving
       * master has a source of Override and a band of Unassigned — it is exactly the broken
       * roster row this report exists to surface, and selecting on source left it out while the
       * "No shift set" tile above the preview counted it.
       */
      return detailRows(records.filter(r => r.band === 'Unassigned'));

    default:
      return detailRows(records);
  }
};

// ─── Columns ──────────────────────────────────────────────────────────────────

export interface ReportColumnDef {
  id: string;
  header: string;
  value: (row: ShiftReportRow) => string | number;
  numeric?: boolean;
  /** Ticked by default in Select Columns. */
  on?: boolean;
}

const rec = (row: ShiftReportRow): ShiftReportRecord | undefined => row.record;
const text = (get: (r: ShiftReportRecord) => string | number | null) =>
  (row: ShiftReportRow): string | number => {
    const r = rec(row);
    if (!r) return '';
    const v = get(r);
    return v === null || v === undefined ? '' : v;
  };
const num = (get: (row: ShiftReportRow) => number | undefined) =>
  (row: ShiftReportRow): number => get(row) || 0;

/**
 * Every column a detail report can show.
 *
 * Trimmed to core roster information alongside the filter panel. Job title, designation, location,
 * manager, employment type, shift type, start/end time, assignment and roster status, the
 * weekday/weekend flag, the shift-change trio, the overtime trio and the four audit columns were
 * all removed on request. The fields remain on ShiftReportRecord, so any of them is one line here
 * away from coming back — nothing upstream had to change.
 */
const DETAIL_COLUMNS: ReportColumnDef[] = [
  { id: 'date',            header: 'Date',              on: true,  value: text(r => r.date) },
  { id: 'weekday',         header: 'Day',               on: true,  value: text(r => r.weekdayName) },
  { id: 'employeeName',    header: 'Employee Name',     on: true,  value: text(r => r.employeeName) },
  { id: 'employeeCode',    header: 'Employee ID',       on: true,  value: text(r => r.employeeCode) },
  { id: 'employeeEmail',   header: 'Email',             value: text(r => r.employeeEmail) },
  { id: 'team',            header: 'Department',        on: true,  value: text(r => r.team) },
  { id: 'employeeStatus',  header: 'Employee Status',   value: text(r => (r.employeeActive ? 'Active' : 'Inactive')) },
  { id: 'shiftCode',       header: 'Shift',             on: true,  value: text(r => r.shiftCode) },
  { id: 'shiftName',       header: 'Shift Name',        on: true,  value: text(r => r.shiftName) },
  { id: 'workingHours',    header: 'Hours',             numeric: true, value: text(r => r.workingHours) },
  { id: 'breakDuration',   header: 'Break (min)',       numeric: true, value: text(r => r.breakDuration) },
  { id: 'source',          header: 'Source',            on: true,  value: text(r => r.source) },
  { id: 'holiday',         header: 'Holiday',           value: text(r => r.holidayName) },
  { id: 'leaveType',       header: 'Leave',             value: text(r => r.leaveType) },
];

const SUMMARY_COLUMNS = (groupHeader: string): ReportColumnDef[] => [
  { id: 'group',        header: groupHeader,     on: true,  value: row => row.groupLabel || '' },
  { id: 'employees',    header: 'Employees',     on: true,  numeric: true, value: num(r => r.employees) },
  { id: 'days',         header: 'Days',          on: true,  numeric: true, value: num(r => r.days) },
  { id: 'shifts',       header: 'Total Shifts',  on: true,  numeric: true, value: num(r => r.shifts) },
  { id: 'morning',      header: 'Morning',       on: true,  numeric: true, value: num(r => r.morning) },
  { id: 'general',      header: 'General',       on: true,  numeric: true, value: num(r => r.general) },
  { id: 'evening',      header: 'Evening',       on: true,  numeric: true, value: num(r => r.evening) },
  { id: 'night',        header: 'Night',         on: true,  numeric: true, value: num(r => r.night) },
  { id: 'weeklyOff',    header: 'Weekly Off',    on: true,  numeric: true, value: num(r => r.weeklyOff) },
  { id: 'holiday',      header: 'Holiday',       on: true,  numeric: true, value: num(r => r.holiday) },
  { id: 'leave',        header: 'On Leave',      on: true,  numeric: true, value: num(r => r.leave) },
  { id: 'unassigned',   header: 'Unassigned',    on: true,  numeric: true, value: num(r => r.unassigned) },
  // Changed and OT-hours totals were dropped with the shift-change and overtime columns they
  // summarised — a group count of a measure the report no longer shows anywhere else is noise.
];

const GROUP_HEADERS: Partial<Record<ShiftReportType, string>> = {
  WEEKLY: 'Week',
  MONTHLY: 'Month',
  EMPLOYEE_WISE: 'Employee',
  DEPARTMENT_WISE: 'Department',
  MANAGER_WISE: 'Manager',
  SHIFT_WISE: 'Shift',
};

/**
 * Columns available for a report, with the default ticks already applied.
 *
 * Report-specific defaults exist because a default set that suits the Complete Roster buries the
 * point of a narrower report: the Unassigned report exists to show who has no shift set, and
 * the Department column it leads with is off by default in the generic set.
 */
export const columnsFor = (type: ShiftReportType): ReportColumnDef[] => {
  const meta = reportMeta(type);
  if (meta.shape === 'SUMMARY') {
    return SUMMARY_COLUMNS(GROUP_HEADERS[type] || 'Group');
  }

  const emphasise = (ids: string[], drop: string[] = []): ReportColumnDef[] =>
    DETAIL_COLUMNS
      .filter(c => !drop.includes(c.id))
      .map(c => (ids.includes(c.id) ? { ...c, on: true } : c));

  switch (type) {
    case 'HOLIDAY_WEEKEND':
      return emphasise(['holiday']);
    case 'UNASSIGNED':
      // Shift name and hours are constant-blank on an unassigned day, so they are dropped rather
      // than offered as columns that can only ever be empty.
      return emphasise(['team'], ['shiftName', 'workingHours', 'breakDuration']);
    default:
      return DETAIL_COLUMNS.map(c => ({ ...c }));
  }
};

/** Human-readable filter list for the export preamble and the preview caption. */
export const describeFilters = (
  filters: ShiftReportFilters,
  range: ResolvedRange
): { label: string; value: string }[] => {
  const out: { label: string; value: string }[] = [{ label: 'Date range', value: range.label }];
  const add = (label: string, value: string) => { if (value) out.push({ label, value }); };

  add('Employee name', filters.employeeName);
  add('Employee ID', filters.employeeCode);
  // The sentinel is an internal value; a downloaded file saying "Department: __UNASSIGNED__"
  // tells the reader nothing about what they are looking at.
  add('Department', filters.team === NO_DEPARTMENT ? 'Not set' : filters.team);
  if (filters.employeeStatus !== 'ALL') add('Employee status', filters.employeeStatus === 'ACTIVE' ? 'Active' : 'Inactive');
  add('Shift', filters.shiftCode);
  if (filters.specificDay !== '') add('Specific day', WEEKDAY_NAMES[Number(filters.specificDay)] || filters.specificDay);
  if (filters.holidayKind !== 'ALL') add('Working / holiday', filters.holidayKind === 'HOLIDAY' ? 'Holidays only' : 'Working days only');

  return out;
};

/** Distinct non-empty values of one field, sorted — populates the filter dropdowns. */
export const distinct = (values: (string | undefined)[]): string[] =>
  Array.from(new Set(values.map(v => (v || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

export const MONTH_LABELS = MONTH_NAMES;
export const WEEKDAY_LABELS = WEEKDAY_NAMES;
